/**
 * Merchant Pickup Windows API Service
 * API calls for managing merchant-specific pickup time window preferences
 */

import api from './api';

export interface TimeWindow {
  start: string; // HH:mm format
  end: string;   // HH:mm format
  crosses_midnight?: boolean; // True if window extends to next day (e.g., 22:00-01:00)
}

export interface MerchantPickupWindow {
  _id: string;
  merchant_id: number;
  merchant_uid: string;
  merchant_name: string;
  time_windows: TimeWindow[];
  time_windows_display?: string;
  requires_dedicated_driver: boolean;
  /** The PA who serves this merchant. Their work is held out of auto-routing for this driver. */
  dedicated_driver_uid?: string;
  dedicated_driver_name?: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateMerchantPickupWindowInput {
  merchantId: number;
  merchantUid: string;
  merchantName: string;
  timeWindows: TimeWindow[];
  requiresDedicatedDriver?: boolean;
  dedicatedDriverUid?: string | null;
  dedicatedDriverName?: string | null;
  notes?: string;
}

export interface UpdateMerchantPickupWindowInput {
  merchantName?: string;
  timeWindows?: TimeWindow[];
  requiresDedicatedDriver?: boolean;
  dedicatedDriverUid?: string | null;
  dedicatedDriverName?: string | null;
  notes?: string;
  isActive?: boolean;
}

/**
 * Get all merchant pickup windows
 */
export async function getMerchantPickupWindows(
  includeInactive = false,
  search?: string
): Promise<MerchantPickupWindow[]> {
  const params = new URLSearchParams();
  if (includeInactive) params.append('includeInactive', 'true');
  if (search) params.append('search', search);
  
  const response = await api.get<{ success: boolean; data: MerchantPickupWindow[] }>(
    `/merchant-pickup-windows?${params.toString()}`
  );
  return response.data.data;
}

/**
 * Get merchant pickup window by ID
 */
export async function getMerchantPickupWindowById(id: string): Promise<MerchantPickupWindow> {
  const response = await api.get<{ success: boolean; data: MerchantPickupWindow }>(
    `/merchant-pickup-windows/${id}`
  );
  return response.data.data;
}

/**
 * Get merchant pickup window by merchant ID
 */
export async function getMerchantPickupWindowByMerchantId(
  merchantId: number
): Promise<MerchantPickupWindow | null> {
  try {
    const response = await api.get<{ success: boolean; data: MerchantPickupWindow }>(
      `/merchant-pickup-windows/merchant/${merchantId}`
    );
    return response.data.data;
  } catch {
    return null;
  }
}

/**
 * Create a new merchant pickup window
 */
export async function createMerchantPickupWindow(
  input: CreateMerchantPickupWindowInput
): Promise<MerchantPickupWindow> {
  const response = await api.post<{ success: boolean; data: MerchantPickupWindow }>(
    '/merchant-pickup-windows',
    input
  );
  return response.data.data;
}

/**
 * Update a merchant pickup window by ID
 */
export async function updateMerchantPickupWindow(
  id: string,
  input: UpdateMerchantPickupWindowInput
): Promise<MerchantPickupWindow> {
  const response = await api.put<{ success: boolean; data: MerchantPickupWindow }>(
    `/merchant-pickup-windows/${id}`,
    input
  );
  return response.data.data;
}

/**
 * Delete a merchant pickup window by ID
 */
export async function deleteMerchantPickupWindow(
  id: string,
  hard = false
): Promise<void> {
  await api.delete(`/merchant-pickup-windows/${id}${hard ? '?hard=true' : ''}`);
}

/**
 * Check which merchants have pickup windows configured
 */
export async function checkMerchantsWithWindows(
  merchantIds: number[]
): Promise<number[]> {
  const response = await api.post<{ success: boolean; data: { merchantIds: number[] } }>(
    '/merchant-pickup-windows/check',
    { merchantIds }
  );
  return response.data.data.merchantIds;
}
